import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { submitMockPayment, submitMockRecharge } from '../lib/api';
import { useReadingFlowBackGuard } from '../lib/navigationGuard';
import type { SpreadType } from '../lib/types';

interface PendingReadingState {
  readingId: string;
  replayToken: string;
  spread?: SpreadType;
}

const PENDING_READING_KEY = 'ny_pending_payment_reading';

type MockMode = 'reading-payment' | 'wallet-recharge';

function isSpreadType(value: string | null | undefined): value is SpreadType {
  return value === 'one-card' || value === 'three-card' || value === 'celtic-cross';
}

function readPendingState(readingId: string): PendingReadingState | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.sessionStorage.getItem(PENDING_READING_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as PendingReadingState;
    if (parsed.readingId === readingId && parsed.replayToken) {
      return parsed;
    }
  } catch {
    return null;
  }

  return null;
}

function clearPendingState(readingId: string) {
  if (typeof window === 'undefined') return;

  const raw = window.sessionStorage.getItem(PENDING_READING_KEY);
  if (!raw) return;

  try {
    const parsed = JSON.parse(raw) as PendingReadingState;
    if (parsed.readingId === readingId) {
      window.sessionStorage.removeItem(PENDING_READING_KEY);
    }
  } catch {
    window.sessionStorage.removeItem(PENDING_READING_KEY);
  }
}

export default function PaymentMockPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const readingId = searchParams.get('readingId');
  const amount = searchParams.get('amount') || '990';
  const label = searchParams.get('label') || '10츄르';
  const points = searchParams.get('points') || '';
  const packageId = searchParams.get('packageId');
  const nextUrl = searchParams.get('next');
  const provider = searchParams.get('provider') || 'kakaopay-demo';
  const mode = (searchParams.get('mode') as MockMode | null) || 'reading-payment';
  const spreadParam = searchParams.get('spread') as SpreadType | null;

  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [completed, setCompleted] = useState(false);

  useReadingFlowBackGuard(Boolean(readingId));

  const pendingState = useMemo(() => {
    if (!readingId) return null;
    return readPendingState(readingId);
  }, [readingId]);

  const replayToken = pendingState?.replayToken || '';
  const spreadFromSession = typeof window !== 'undefined'
    ? window.sessionStorage.getItem('selected_spread')
    : null;
  const spread = (
    (isSpreadType(spreadParam) ? spreadParam : null)
    || (isSpreadType(pendingState?.spread) ? pendingState.spread : null)
    || (isSpreadType(spreadFromSession) ? spreadFromSession : null)
    || 'three-card'
  );

  const handlePay = async () => {
    if (mode === 'reading-payment' && !readingId) {
      setErrorMessage('리딩 ID가 없습니다.');
      return;
    }

    if (mode === 'wallet-recharge' && !packageId) {
      setErrorMessage('충전 패키지 정보가 없습니다.');
      return;
    }

    if (processing || completed) return;

    setProcessing(true);
    setErrorMessage('');

    try {
      if (mode === 'wallet-recharge') {
        await submitMockRecharge(packageId!);
      } else {
        const txPrefix = 'mock_';
        const transactionId = `${txPrefix}${Date.now()}-${crypto.randomUUID()}`;
        await submitMockPayment(readingId!, transactionId, spread);
        clearPendingState(readingId!);
        if (replayToken) {
          window.sessionStorage.setItem(`reading_${readingId!}`, replayToken);
        }
        window.sessionStorage.setItem('selected_spread', spread);
      }

      setCompleted(true);

      if (nextUrl) {
        navigate(nextUrl);
        return;
      }

      if (mode === 'wallet-recharge') {
        navigate(`/wallet/recharge?recharged=1&packageId=${encodeURIComponent(packageId!)}`);
        return;
      }

      navigate(`/spread-layout/${readingId}`);
    } catch (error) {
      const apiError = error as Error & { code?: string };
      if (mode === 'reading-payment' && apiError.code === 'ALREADY_PAID') {
        navigate(`/spread-layout/${readingId}`);
        return;
      }
      setErrorMessage(apiError.message || '결제 처리에 실패했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#15110f] px-4 py-10 text-[#f5ede1]">
      <div className="mx-auto w-full max-w-md rounded-[32px] border border-[#f4d159]/30 bg-[#fbf7ef] p-6 text-[#1d160f] shadow-[0_40px_90px_-40px_rgba(0,0,0,0.85)]">
        <div className="rounded-[26px] bg-[#f8db53] px-5 py-5 text-[#241811]">
          <p className="text-[11px] uppercase tracking-[0.24em] text-[#6f4b16]">
            {provider === 'kakaopay-demo' ? 'KakaoPay Demo' : 'Mock Checkout'}
          </p>
          <h1 className="mt-2 text-2xl font-black tracking-tight">
            {mode === 'wallet-recharge' ? '카카오페이 데모 결제' : '결제 테스트'}
          </h1>
          <p className="mt-2 text-sm leading-6 text-[#59431a]">
            실제 결제는 일어나지 않지만, 심사 확인용으로 결제 흐름과 충전 반영 결과를 재현하는 화면입니다.
          </p>
        </div>

        <div className="mt-5 rounded-[26px] border border-[#e3d8c7] bg-white px-5 py-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#907761]">주문 상품</p>
              <p className="mt-2 text-xl font-semibold text-[#18110e]">
                {mode === 'wallet-recharge' ? `${label} 충전` : '묘안 리딩'}
              </p>
              {mode === 'wallet-recharge' && points && (
                <p className="mt-2 text-sm text-[#6d5647]">충전 후 {points}를 바로 사용할 수 있습니다.</p>
              )}
              {mode === 'reading-payment' && readingId && (
                <p className="mt-2 text-sm text-[#6d5647]">리딩 ID {readingId.slice(0, 8)} 기준 결제 흐름입니다.</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#907761]">최종 금액</p>
              <p className="mt-2 text-3xl font-black text-[#18110e]">{Number(amount || 0).toLocaleString()}원</p>
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-[#ecdcb6] bg-[#fff9df] px-4 py-4">
            <p className="text-sm font-semibold text-[#4e3d14]">결제 방법</p>
            <div className="mt-3 grid gap-2">
              <div className="flex items-center justify-between rounded-xl border border-[#d6c39a] bg-white px-4 py-3">
                <span className="font-semibold text-[#241811]">카카오페이</span>
                <span className="rounded-full bg-[#f7de52] px-3 py-1 text-[11px] font-bold text-[#241811]">선택됨</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-sm text-[#7a624a]">
                <div className="rounded-xl border border-[#eadfce] bg-[#faf7f1] px-2 py-3">Npay</div>
                <div className="rounded-xl border border-[#eadfce] bg-[#faf7f1] px-2 py-3">계좌이체</div>
                <div className="rounded-xl border border-[#eadfce] bg-[#faf7f1] px-2 py-3">카드</div>
              </div>
            </div>
          </div>

          <label className="mt-5 flex items-start gap-3 text-sm leading-6 text-[#5f4a3d]">
            <input type="checkbox" checked readOnly className="mt-1 h-4 w-4 rounded border-[#cabba8]" />
            <span>[필수] 결제 서비스 이용약관, 개인정보처리방침에 동의합니다.</span>
          </label>
        </div>

        {errorMessage && (
          <p className="mt-4 rounded-2xl border border-[#f4d4cf] bg-[#fff6f5] px-4 py-3 text-sm text-[#8b3d34]">
            {errorMessage}
          </p>
        )}

        <button
          type="button"
          onClick={handlePay}
          disabled={
            (mode === 'reading-payment' && !readingId)
            || (mode === 'wallet-recharge' && !packageId)
            || processing
            || completed
          }
          className="mt-5 inline-flex w-full items-center justify-center rounded-2xl bg-[#f7de52] px-5 py-4 text-base font-black text-[#241811] transition hover:bg-[#efd33e] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {completed ? '처리 완료' : processing ? '결제 처리 중...' : `${Number(amount || 0).toLocaleString()}원 결제하기`}
        </button>

        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mt-4 inline-flex w-full items-center justify-center rounded-2xl border border-[#d5c4b0] bg-white px-5 py-4 text-sm font-semibold text-[#3b2d24]"
        >
          이전 화면으로 돌아가기
        </button>
      </div>
    </div>
  );
}
